// Presentation helpers shared across the marketing pages: channel labels,
// icons, and status → badge-variant mapping. Pure mapping only — no data
// access, no business decisions.

import { Mail, Phone, Package, Send, type LucideIcon } from "lucide-react";
import type {
  CampaignStatus,
  CampaignSummary,
  ConsentState,
  MarketingChannel,
} from "@shared-core/data/marketing-types";
import { hasSent } from "@shared-core/domain/campaign-sends";

export const CHANNEL_LABELS: Record<MarketingChannel, string> = {
  email: "Email",
  call: "Call",
  enclosure: "Enclosure",
  direct_mail: "Direct mail",
};

export const CHANNEL_ICONS: Record<MarketingChannel, LucideIcon> = {
  email: Mail,
  call: Phone,
  enclosure: Package,
  direct_mail: Send,
};

/** Execution target chip text for a channel — "→ Mailchimp" etc. */
export const CHANNEL_TARGETS: Record<MarketingChannel, string> = {
  email: "→ Mailchimp",
  call: "→ Rep tickets",
  enclosure: "→ CSV / despatch",
  direct_mail: "→ CSV export",
};

type BadgeVariant =
  | "default"
  | "secondary"
  | "outline"
  | "success"
  | "warning"
  | "error"
  | "neutral";

export function campaignStatusVariant(status: CampaignStatus): BadgeVariant {
  switch (status) {
    case "active":
      return "success";
    case "draft":
      return "warning";
    case "closed":
      return "neutral";
    case "archived":
      return "outline";
    case "legacy":
      return "secondary";
  }
}

/** The campaign-projects glossary's status words — used identically at both
 *  the project rollup level and the individual send level, never a
 *  different word per level. "active"/"closed" read as "Live"/"Complete" in
 *  the UI; archived/legacy (whole-project-only states) keep their own word. */
export function campaignStatusLabel(status: CampaignStatus): string {
  switch (status) {
    case "draft":
      return "Draft";
    case "active":
      return "Live";
    case "closed":
      return "Complete";
    case "archived":
      return "Archived";
    case "legacy":
      return "Legacy";
  }
}

/** A single send's badge. A send Mailchimp has already sent reads "Sent"
 *  rather than "Live" — "Live" on an email that went out weeks ago misreads
 *  as still in progress, and a sent step never leaves "active" (see
 *  hasSent). Project-level badges keep campaignStatusLabel. */
export function sendStatusLabel(step: {
  status: CampaignStatus;
  sent_at: string | null;
}): string {
  return step.status === "active" && hasSent(step)
    ? "Sent"
    : campaignStatusLabel(step.status);
}

export function sendStatusVariant(step: {
  status: CampaignStatus;
  sent_at: string | null;
}): BadgeVariant {
  return step.status === "active" && hasSent(step)
    ? "neutral"
    : campaignStatusVariant(step.status);
}

/** Which status a campaign card/header should actually show: `status` for
 *  the two whole-project terminal states (archived/legacy), otherwise the
 *  denormalized send-progress rollup — never the raw draft/active/closed
 *  `status`, which is a different, project-lifecycle concept once a
 *  campaign has multiple sends. */
export function campaignDisplayStatus(
  campaign: Pick<CampaignSummary, "status" | "rollup_status">,
): CampaignStatus {
  return campaign.status === "archived" || campaign.status === "legacy"
    ? campaign.status
    : campaign.rollup_status;
}

export function consentVariant(state: ConsentState): BadgeVariant {
  return state === "allowed" ? "success" : "error";
}
